/**
 * Bulk distribution API — registers a whole saved postal code set, or a whole assignment
 * area, as delivered by one distributor (e.g. the PostNord system account).
 */
import { apiClient } from "./client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type BulkDistributionSourceType = "SavedSet" | "Assignment";

export interface BulkDistributionSource {
    sourceType: BulkDistributionSourceType;
    id: string;
    name: string;
    municipalityPolygonId?: string | null;
    municipalityName?: string | null;
    /** Set for assignments that already belong to a campaign. */
    campaignId?: string | null;
    /**
     * Flygbladet uppdraget ska dela ut — valet för den senast startade öppna omgången i
     * utdelningslistan, annars uppdragets eget flygblad. Förväljs i panelen.
     */
    flyerId?: string | null;
    flyerName?: string | null;
}

export interface BulkDistributionDistributor {
    id: string;
    name: string;
    /** True for PostNord / Svensk Direktreklam — accounts nobody logs in to. */
    isSystemAccount: boolean;
}

export interface BulkDistributionRequest {
    sourceType: BulkDistributionSourceType;
    sourceId: string;
    campaignId: string;
    flyerId: string;
}

export interface BulkDistributionRunRequest extends BulkDistributionRequest {
    distributorUserId: string;
    /** ISO date (yyyy-MM-dd). Defaults to today on the server. */
    deliveredOn?: string;
}

export interface BulkDistributionPreviewItem {
    label: string;
    addressCount: number;
    alreadyDeliveredCount: number;
}

export interface BulkDistributionPreview {
    sourceName: string;
    campaignName: string;
    flyerName: string;
    addressCount: number;
    alreadyDeliveredCount: number;
    wouldCreateCount: number;
    skippedHiddenOrBlockedCount: number;
    items: BulkDistributionPreviewItem[];
}

export interface BulkDistributionRunResult {
    runId: string;
    createdCount: number;
    skippedCount: number;
}

export interface BulkDistributionRun {
    runId: string;
    sourceType: BulkDistributionSourceType;
    sourceName: string;
    campaignId: string;
    campaignName: string;
    flyerName: string;
    distributorName: string;
    deliveredAt: string;
    recordedAt: string;
    addressCount: number;
    totalQuantity: number;
}

const BASE = "/bulk-distribution";

export const fetchBulkDistributionSources = async (): Promise<BulkDistributionSource[]> => {
    const { data } = await apiClient.get<BulkDistributionSource[]>(`${BASE}/sources`);
    return data;
};

export const fetchBulkDistributionDistributors = async (): Promise<BulkDistributionDistributor[]> => {
    const { data } = await apiClient.get<BulkDistributionDistributor[]>(`${BASE}/distributors`);
    return data;
};

export const previewBulkDistribution = async (
    request: BulkDistributionRequest
): Promise<BulkDistributionPreview> => {
    const { data } = await apiClient.post<BulkDistributionPreview>(`${BASE}/preview`, request);
    return data;
};

export const runBulkDistribution = async (
    request: BulkDistributionRunRequest
): Promise<BulkDistributionRunResult> => {
    const { data } = await apiClient.post<BulkDistributionRunResult>(`${BASE}/run`, request);
    return data;
};

export const fetchBulkDistributionRuns = async (campaignId?: string): Promise<BulkDistributionRun[]> => {
    const params = campaignId ? `?campaignId=${campaignId}` : "";
    const { data } = await apiClient.get<BulkDistributionRun[]>(`${BASE}/runs${params}`);
    return data;
};

export const undoBulkDistributionRun = async (runId: string): Promise<{ deletedCount: number }> => {
    const { data } = await apiClient.delete<{ deletedCount: number }>(`${BASE}/runs/${runId}`);
    return data;
};

export const useBulkDistributionSources = () =>
    useQuery({
        queryKey: ["bulk-distribution", "sources"],
        queryFn: fetchBulkDistributionSources,
        staleTime: 60 * 1000
    });

export const useBulkDistributionDistributors = () =>
    useQuery({
        queryKey: ["bulk-distribution", "distributors"],
        queryFn: fetchBulkDistributionDistributors,
        staleTime: 5 * 60 * 1000
    });

export const useBulkDistributionRuns = (campaignId?: string) =>
    useQuery({
        queryKey: ["bulk-distribution", "runs", campaignId ?? "all"],
        queryFn: () => fetchBulkDistributionRuns(campaignId)
    });

export const usePreviewBulkDistribution = () =>
    useMutation({ mutationFn: previewBulkDistribution });

export const useRunBulkDistribution = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: runBulkDistribution,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["bulk-distribution", "runs"] });
            queryClient.invalidateQueries({ queryKey: ["map", "addresses"] });
            queryClient.invalidateQueries({ queryKey: ["deliveries"] });
        }
    });
};

export const useUndoBulkDistributionRun = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: undoBulkDistributionRun,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["bulk-distribution", "runs"] });
            queryClient.invalidateQueries({ queryKey: ["map", "addresses"] });
            queryClient.invalidateQueries({ queryKey: ["deliveries"] });
        }
    });
};
